import * as React from 'react';
import { resolveSyncIfLoadedOrThrow } from '@avensia/excite';
import {
  circularJsonStringify,
  ComponentTypeEnum,
  type ExperienceConfiguration,
  type Reference,
} from '@ninetailed/experience.js-shared';
import { useNinetailed } from '@ninetailed/experience.js-react';
import { HydrationOptionsProvider } from '@avensia/excite/progressive-hydration';
import { getExperienceWithSelectedVariantId as getExperiencesWithSelectedVariant, ninetailedTrack } from '.';
import type {
  NinetailedExperienceComponentProps,
  ExperienceContextType,
  ExperienceBaseProps,
  UseExperienceReturn,
} from './types';
import { isExperienceValid, getVariantName, DEFAULT_EXPERIENCE_FALLBACK, NINETAILED_CLICK_EVENT } from './utils';

export const useExciteExperience = <TBaseline extends Reference, TVariant extends Reference>(
  baselineProps: Record<string, unknown>,
  experience?: ExperienceConfiguration<TVariant>,
  id?: string,
): UseExperienceReturn<TBaseline, TVariant> => {
  const ninetailed = useNinetailed<TBaseline, TVariant>();

  // Create baseline from props and id
  const baseline = React.useMemo(() => ({ id, ...baselineProps }) as TBaseline, [id, baselineProps]);

  const experienceWithSelectedVariant = getExperiencesWithSelectedVariant();

  // Calculate better initial state
  const initialVariantIndex = experience ? experienceWithSelectedVariant[experience?.id] : 0;

  const variants = experience?.components[0].variants;

  const initialVariant: TBaseline | TVariant = (
    experience && experienceWithSelectedVariant[experience?.id] && variants?.[initialVariantIndex - 1]
      ? variants[initialVariantIndex - 1]
      : baseline
  ) as TBaseline | TVariant;

  const [experienceState, setExperienceState] = React.useState<UseExperienceReturn<TBaseline, TVariant>>({
    hasVariants: variants ? true : false,
    baseline,
    error: null,
    loading: true,
    status: 'loading',
    experience: experience ?? null,
    variant: initialVariant,
    variantIndex: initialVariantIndex,
    audience: null,
    isPersonalized: false,
    profile: null,
    componentProps: baselineProps,
    isValid: false,
  });

  React.useEffect(() => {
    if (experience) {
      for (const component of experience.components) {
        component.type = ComponentTypeEnum.EntryReplacement;
      }

      return ninetailed.onSelectVariant({ baseline, experiences: [experience] }, changes => {
        const isValid = isExperienceValid(changes.status, changes.experience, changes.audience, changes.variant);
        const props = isValid ? (changes.variant as Record<string, unknown>) : baselineProps;

        // Optional field contentful that is required for Ninetailed tracking
        if (experience && !experience?.description) {
          experience.description = DEFAULT_EXPERIENCE_FALLBACK;
        }

        if (isValid) {
          setExperienceState({
            ...(changes as UseExperienceReturn<TBaseline, TVariant>),
            componentProps: props,
            isValid: isValid,
          });
        }
      });
    }
  }, [circularJsonStringify(baseline), circularJsonStringify(experience), baselineProps]);

  return experienceState;
};

export const useExciteExperienceContent = <T extends Reference>(
  baselineProps: T & { experiences: Excite.Ninetailed.Experience[] },
): T => {
  const experienceInfo = useExciteExperience<T, T>(
    baselineProps,
    baselineProps.experiences?.[0]?.experienceModel,
    baselineProps.id,
  );
  return experienceInfo.variant as T;
};

const CurrentExperienceContext = React.createContext<ExperienceContextType | null>(null);

export function useCurrentExperience(): ExperienceContextType | null {
  return React.useContext(CurrentExperienceContext);
}

/**
 * Shared hook for element tracking with Ninetailed
 * @param componentRef - Reference to the component element to track
 * @param experienceResult - The result from useExciteExperience hook (optional, uses context if not provided)
 * @param activeTracking - Whether tracking is enabled
 */
function useElementTracking<T extends Element>(
  componentRef: React.RefObject<T | null>,
  experienceResult?: UseExperienceReturn<any, any> | null,
  activeTracking: boolean = true,
): void {
  const { observeElement, unobserveElement } = useNinetailed();
  const currentExperience = useCurrentExperience();

  React.useEffect(() => {
    const componentElement = componentRef.current;

    if (!componentElement || !activeTracking) {
      return;
    }

    // Use provided experienceResult or fall back to context
    const result = experienceResult || currentExperience;

    if (result?.isValid) {
      observeElement({
        element: componentElement,
        experience: result.experience,
        audience: result.audience,
        variant: result.variant,
        variantIndex: result.variantIndex,
        componentType: 'Entry',
      });
    }

    return () => {
      unobserveElement(componentElement);
    };
  }, [componentRef, experienceResult, activeTracking, currentExperience, observeElement, unobserveElement]);
}

/**
 * Hook to enable tracking for an experience component
 * @param componentRef - Reference to the component element to track
 */
export function useTrackingBlock(componentRef: React.RefObject<HTMLDivElement | null>): void {
  useElementTracking(componentRef);
}

/**
 * @param feature - The feature name to test
 * @param featureExperiences - The feature test API model
 * @param activeTracking - Whether to enable active tracking
 */
export function useAvensiaNinetailedFeatureTest<T extends HTMLElement>(
  feature: string,
  featureExperiences: Excite.Ninetailed.NinetailedFeatureTestApiModel,
  activeTracking: boolean = true,
) {
  const featureExperience = featureExperiences?.tests?.[feature];
  const componentRef = React.useRef<T>(null);

  const baseline = featureExperience?.experienceModel?.components[0]?.baseline;
  const experienceResult = useExciteExperience(baseline ?? {}, featureExperience?.experienceModel);
  useElementTracking(componentRef, experienceResult, activeTracking);

  return {
    variantKey: (experienceResult?.variant as unknown as Excite.Ninetailed.FeatureVariant)?.variantKey,
    componentRef,
  };
}

export function useNinetailedClickEvent() {
  const info = useCurrentExperience();

  return React.useCallback(() => {
    if (info?.isValid) {
      const variantName = getVariantName(info.variant);
      const experienceId = info.experience?.id ?? DEFAULT_EXPERIENCE_FALLBACK;
      const experienceName = info.experience?.name ?? DEFAULT_EXPERIENCE_FALLBACK;

      const trackingData = {
        ninetailed_variant: variantName,
        ninetailed_experience: experienceId,
        ninetailed_experience_name: experienceName,
      };

      ninetailedTrack('click', trackingData);
      window.dataLayer.push({
        event: NINETAILED_CLICK_EVENT,
        ...trackingData,
      });
    }
  }, [info]);
}

function ExciteExperience({
  experience,
  component: BaselineComponent,
  passthroughProps: baselineProps = {},
  id,
}: ExperienceBaseProps) {
  const experienceInfo = useExciteExperience(baselineProps, experience, id);
  const VariantComponent = resolveSyncIfLoadedOrThrow((experienceInfo.variant as any).componentName);
  const Component = VariantComponent ?? BaselineComponent;
  return (
    <HydrationOptionsProvider renderInsteadOfHydrate>
      <CurrentExperienceContext.Provider value={{ ...experienceInfo }}>
        <Component {...experienceInfo.variant} />
      </CurrentExperienceContext.Provider>
    </HydrationOptionsProvider>
  );
}

export const NinetailedExperience = React.memo(function Experience({
  item,
  index,
}: NinetailedExperienceComponentProps) {
  const componentName = item.componentName ?? '';
  const Component = resolveSyncIfLoadedOrThrow(componentName);
  const experiences = (item.nt_experiences ?? []).map(experience => ({
    ...experience.experienceModel,
  }));

  const componentProps = { ...item, index };

  return experiences.length > 0 ? (
    <ExciteExperience
      id={item.id}
      component={Component}
      experience={experiences[0]}
      passthroughProps={componentProps}
    />
  ) : (
    <Component {...componentProps} />
  );
});
